import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { syncGiftAfterAdd } from '@theme/buy-x-get-y-sync';
import { describeRule, loadSnapshot } from '@theme/gwp-engine';

/**
 * The product page's free-gift banner (blocks/buy-x-get-y.liquid), alive. Two jobs:
 *
 * 1. TELL the shopper where the cart stands against the promotion — "Tambah belanja Rp149,200 lagi
 *    untuk dapat hadiah gratis", or "Selamat! … sudah masuk keranjang". It is the cart page's sentence,
 *    word for word, computed from the same cart (assets/gwp-engine.js): the product being looked at is
 *    deliberately NOT counted in, so the product page, the quick-add modal and the cart never give two
 *    different numbers for the same cart. The wording is a locale template (`gwp_*`), never written
 *    here.
 *
 * 2. ADD the gift once this product's own Add to Cart succeeds — but only if the promotion's rule is
 *    met (assets/buy-x-get-y-sync.js). Not met → no gift, and the "add more" message is handed to
 *    the add-to-cart card as a `buy-x-get-y:gift-pending` event so the shopper is told at the moment
 *    of adding. Gift quantity and later edits on the cart page are the sync's business too
 *    (assets/buy-x-get-y-cart-sync.js) — this component only runs on the product page and isn't in the
 *    DOM once the shopper leaves it.
 *
 * Listens for the same `CartLinesUpdateEvent` this theme's `product-form-component` already
 * dispatches on a successful add (see assets/product-form.js `#processAddToCart`), instead of
 * hooking `window.fetch` — `cart-items-component` (assets/component-cart-items.js
 * `#handleCartUpdate`) already listens for *any* `CartLinesUpdateEvent` on `document` with no
 * context filter, so the sync dispatching one for the gift add/adjust is enough to make it refresh
 * the cart UI through its existing `sectionRenderer` fallback path.
 *
 * The sync's own dispatched event uses `context: 'standard-action'` (a real, typed
 * `CartLinesUpdatePayload` context distinct from `'product'`) so this component's own add listener,
 * which only reacts to `context === 'product'`, can never re-trigger itself.
 *
 * `data-auto-add="false"` (the block's "Auto add gift to cart" setting) switches both jobs off: the
 * banner just names the gift, and neither promises nor adds anything.
 *
 * @typedef {object} BuyXGetYRefs
 * @property {HTMLElement} [status]
 *
 * @extends Component<BuyXGetYRefs>
 */
export class BuyXGetYAutoAdd extends Component {
  #abortController = new AbortController();

  /** @type {{ cart: any, rules: import('@theme/gwp-engine').GiftRule[] } | null} */
  #snapshot = null;

  /** @type {number | undefined} */
  #refreshTimer;

  /**
   * Repaints when a morph resets the status line to the server's empty shell (Component's own
   * `updatedCallback` never runs for the live element — morph calls it on the unupgraded node it
   * parsed the new HTML into, see assets/gwp-progress.js). Filtered to the status line itself so the
   * rest of the banner (the image dialog, …) can't make it work.
   */
  #observer = new MutationObserver((records) => {
    const { status } = this.refs;
    if (status && records.some((record) => record.target === status || status.contains(record.target))) this.#render();
  });

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['hidden', 'data-state'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#observer.disconnect();
    clearTimeout(this.#refreshTimer);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const triggerProductId = this.dataset.triggerProductId;
        const isOwnAdd =
          event.action === 'add' && event.context === 'product' && String(detail?.productId) === triggerProductId;

        if (isOwnAdd && triggerProductId && this.#autoAdd) {
          syncGiftAfterAdd(this, triggerProductId, detail?.items).then(() => this.#refresh());
          return;
        }

        // Anything else that changed the cart (a gift added or stripped, another card, the cart
        // page in another place) moves the numbers the message is built from.
        this.#scheduleRefresh();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[buy-x-get-y] Event promise rejected:', error);
      });
  };

  get #autoAdd() {
    return this.dataset.autoAdd === 'true';
  }

  #scheduleRefresh() {
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = window.setTimeout(() => this.#refresh(), 250);
  }

  async #refresh() {
    if (!this.#autoAdd) return;

    // This block's own product, not the page's: inside the quick-add modal (a product page's blocks
    // shown on a collection page, or over another product) `location` is somebody else's page.
    const snapshot = await loadSnapshot({ productUrl: this.dataset.productUrl || window.location.pathname });
    if (!snapshot || !this.isConnected) return;

    this.#snapshot = snapshot;
    this.#render();
  }

  /** Writes only where the DOM differs, so the observer above settles instead of feeding on itself. */
  #render() {
    const { status } = this.refs;
    if (!status) return;

    const rule = this.#snapshot?.rules.find((candidate) => String(candidate.gift_product_id) === this.dataset.giftProductId);
    if (!rule || !this.#autoAdd) {
      if (!status.hidden) status.hidden = true;
      return;
    }

    const result = describeRule(rule, this.#snapshot?.cart.items ?? []);

    if (status.textContent !== result.message) status.textContent = result.message;
    if (status.dataset.state !== result.state) status.dataset.state = result.state;
    if (status.hidden !== !result.message) status.hidden = !result.message;
  }
}

if (!customElements.get('buy-x-get-y-auto-add')) {
  customElements.define('buy-x-get-y-auto-add', BuyXGetYAutoAdd);
}
